import { useEffect, useState } from 'react';
import { API, normalizeMediaUrl } from './apiBase';

let _cache = null;
const _subs = new Set();

const broadcast = () => _subs.forEach((fn) => fn(_cache));

// Normalize any absolute "/api/files/..." media URLs to same-origin so slots
// saved with a hard-coded host still load on the current domain.
const _normalizeCache = (c) => {
  if (!c || typeof c !== 'object') return c;
  const out = {};
  for (const [slot, v] of Object.entries(c)) {
    out[slot] = v && Array.isArray(v.media)
      ? { ...v, media: v.media.map((m) => ({ ...m, url: normalizeMediaUrl(m.url) })) }
      : v;
  }
  return out;
};

const refresh = async () => {
  try {
    const r = await fetch(`${API}/api/site-media`, { cache: 'no-store' });
    if (!r.ok) return;
    _cache = _normalizeCache(await r.json());
    broadcast();
  } catch (e) { console.warn('[useSiteMedia] refresh failed (keeping cache):', e); }
};

if (typeof window !== 'undefined') {
  window.addEventListener('focus', refresh);
}

/**
 * useSiteMedia(slot) — returns `{ media: [...], transition, duration_ms, transition_ms }`
 * for the given slot. Falls back to `{ media: [] }` while loading or on miss,
 * so components can safely render their built-in default media.
 *
 * Note: `_cache` and `_subs` are module-scoped pub/sub refs — intentionally
 * not React state, so they're not part of useEffect deps.
 */
export const useSiteMedia = (slot) => {
  const [state, setState] = useState(_cache ? _cache[slot] : null);
  useEffect(() => {
    const handler = (c) => setState(c ? c[slot] : null);
    _subs.add(handler);
    if (!_cache) refresh();
    return () => _subs.delete(handler);
  }, [slot]);
  return state || { media: [], transition: 'fade', duration_ms: 5000, transition_ms: 900 };
};

export const refreshSiteMedia = refresh;
